import api from '../axios.config';
import { API_ENDPOINTS, createEndpoint } from './endpoints';

// Backend expects a bare 10-digit number — strip the +91 country code / symbols.
const normalizeIndianPhone = (phone) => {
  if (!phone) return phone;
  const digits = String(phone).replace(/\D/g, '');
  return digits.length > 10 && digits.startsWith('91')
    ? digits.slice(-10)
    : digits;
};

const STATUS_MAPPING = {
  submitted: ['SUBMITTED'],
  shortlisted: ['SHORTLISTED'],
  pending_interview: ['PENDING_INTERVIEW'],
  offer_extended: ['OFFER_EXTENDED'],
  offer_accepted: ['OFFER_ACCEPTED'],
  joined: ['JOINED'],
  screened: ['SCREENED'],
  accepted: ['ACCEPTED'],
  rejected: ['REJECTED'],
  pending: ['PENDING'],
  in_progress: ['IN_PROGRESS'],
  passed: ['PASSED'],
  failed: ['FAILED'],
};

export const CandidateService = {
  // getCandidateDetails: async (id) => {
  //   const endpoint = createEndpoint(API_ENDPOINTS.SEARCH.GETCANDIDATEDETAILS, {
  //     id,
  //   });
  //   const response = await api.get(endpoint);
  //   return response;
  // },

  // Fetch source candidates for a requirement location
  listCandidates: async (
    opportunityId,
    requirementLocationId,
    pageNo,
    pageSize
  ) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.LIST, {
      id: opportunityId,
      requirementLocationId,
      pageNo,
      pageSize,
    });
    const response = await api.get(endpoint);
    return response;
  },

  submitCandidates: async (id, requirementLocationId, candidates = []) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.SUBMITCANDIDATE, {
      id,
    });
    const payload = {
      requirement_location_id: requirementLocationId,
      candidates,
    };
    const response = await api.post(endpoint, payload);
    return response;
  },

  getCandidatesByStatus: async ({
    opportunityId,
    status,
    page,
    pageSize,
    requirementLocationId,
  }) => {
    const mappedStatus = STATUS_MAPPING[status]
      ? STATUS_MAPPING[status][0]
      : status;
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.LISTBYSTATUS, {
      id: opportunityId,
      requirementLocationId,
      pageNo: page,
      pageSize,
      status: mappedStatus,
    });
    const response = await api.get(endpoint);
    return response;
  },
  deleteCandidates: async (id, data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.DELETECANDIDATE, {
      id,
    });
    const response = await api.delete(endpoint, { data });
    return response;
  },
  getCandidatesByStatusForTa: async ({
    assignmentId,
    requirementLocationId,
    status,
    page,
    pageSize,
    search,
  }) => {
    // const mappedStatus = STATUS_MAPPING[status]
    //   ? STATUS_MAPPING[status][0]
    //   : status;
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.LISTBYSTATUSFORTA,
      {
        assignmentId,
        requirementLocationId,
        pageNo: page,
        pageSize,
        status,
        search: search || undefined,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  getExternalCandidates: async ({
    assignmentId,
    page,
    pageSize,
    source = 'Direct',
  }) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.LISTEXTERNALCANDIDATES,
      {
        assignmentId,
        pageNo: page,
        pageSize,
        source,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  getRequirementMatches: async (
    opportunityId,
    { page = 1, pageSize = 10, search = '', tier = null } = {}
  ) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.REQUIREMENT_MATCHES,
      {
        id: opportunityId,
        page,
        pageSize,
        search: search || null,
        tier,
      }
    );
    return api.get(endpoint);
  },

  // Fetch the enforcement modes (drop / flag / boost) for each constraint
  // on a requirement. `allowed_modes` + "off" (remove) drive the popover.
  getRequirementConstraintModes: async (requirementId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.CONSTRAINT_MODES, {
      id: requirementId,
    });
    return api.get(endpoint);
  },

  // Save a constraint's mode (Drop / Flag / Boost) — mode is sent uppercase.
  setRequirementConstraintMode: async (requirementId, field, mode) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.SET_CONSTRAINT_MODE,
      { id: requirementId, field }
    );
    return api.put(endpoint, { mode: String(mode).toUpperCase() });
  },

  // Remove a constraint's rule (the "Off" mode) — no body.
  removeRequirementConstraintMode: async (requirementId, field) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.SET_CONSTRAINT_MODE,
      { id: requirementId, field }
    );
    return api.delete(endpoint);
  },

  listCandidatesFromSecondary: async (
    assignmentId,
    requirementLocationId,
    candidates = []
  ) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.LISTFROMSECONDARY,
      {
        assignmentId,
        requirementLocationId,
      }
    );
    const payload = {
      requirement_location_id: requirementLocationId,
      candidates,
    };
    const response = await api.get(endpoint, payload);
    return response;
  },
  getCandidateScores: async (candidateId, opportunityId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.GETCANDIDATESCORES,
      {
        candidate_id: candidateId,
        requirement_id: opportunityId,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  updateScore: async (data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.UPDATESCORE, {});
    const response = await api.post(endpoint, data);
    return response;
  },
  sendTestLink: async (data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.SENDTESTLINK, {});
    const response = await api.post(endpoint, data);
    return response;
  },
  assignTest: async (data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.ASSIGNTEST, {});
    const response = await api.post(endpoint, data);
    return response;
  },
  // Generic, data-driven manual-flow-action executor — per the "Flow State
  // Management" API guide (PR #748): the action key comes from a status's
  // own `manual_actions` list (GET .../flow-statuses), never hardcoded here.
  // `secMappingId` is this app's existing "mapping id" concept (the
  // candidate↔requirement pairing, e.g. AssignTestAction's mapping_ids) —
  // not `candidate_id`. `payload` is optional; only sent when present.
  executeFlowAction: async (secMappingId, actionKey, payload) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.EXECUTE_FLOW_ACTION,
      { secMappingId, actionKey }
    );
    const response = await api.post(
      endpoint,
      payload ? { payload } : undefined
    );
    return response;
  },
  // Backend-computed skill match for one candidate↔requirement mapping —
  // the requirement's required_skills (each with the candidate's own
  // evidenced proficiency/experience/recency where already known) plus
  // extra_core_skills the candidate has beyond what's required. Powers the
  // Skills tab's live prefill in UpdateProfileDetailsModal; only meaningful
  // for a single candidate, so callers only fetch this when exactly one is
  // selected.
  getCandidateSkillMatch: async (secMappingId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.SKILL_MATCH, {
      secMappingId,
    });
    return api.get(endpoint);
  },
  // Same URL as the GET above, PATCH to persist what the Skills tab
  // (UpdateProfileDetailsModal) captured — confirmed live as its own
  // endpoint, separate from flow-actions/interview_profile_update (which
  // only covers the Details tab's fields). `skills` is
  // [{ name, years, months, proficiency }] — no last_used field in the
  // confirmed contract, unlike this form's own local shape. Method changed
  // from POST to PATCH per explicit backend update — was POST when this
  // contract was first confirmed live.
  updateCandidateSkillMatch: async (secMappingId, skills) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.SKILL_MATCH, {
      secMappingId,
    });
    return api.patch(endpoint, { skills });
  },
  // Prefill source for the reschedule_slot modal — the candidate's existing
  // interview slot, so a TA reopening it to change the time doesn't have to
  // re-enter everything from scratch. Keyed by secMappingId, same as
  // executeFlowAction/getCandidateSkillMatch above (an earlier version of
  // this endpoint keyed on assignmentId + the real candidate_id instead —
  // since dropped).
  getInterviewSchedule: async (secMappingId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.GET_INTERVIEW_SCHEDULE,
      { secMappingId }
    );
    return api.get(endpoint);
  },
  completeCandidateScore: async (candidateId, opportunityId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.COMPLETECANDIDATESCORE,
      {
        candidate_id: candidateId,
        requirement_id: opportunityId,
      }
    );
    const response = await api.post(endpoint);
    return response;
  },
  submitSecondaryCandidates: async (
    assignmentId,
    requirementLocationId,
    candidates = []
  ) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.SUBMITSECONDARYCANDIDATE,
      {
        assignmentId,
      }
    );
    const payload = {
      requirement_location_id: requirementLocationId,
      candidates,
    };
    const response = await api.post(endpoint, payload);
    return response;
  },
  updateTestStatus: async (data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.UPDATETESTSTATUS,
      {}
    );
    const response = await api.post(endpoint, data);
    return response;
  },
  saveJoiningDetails: async (data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.SAVEJOININGDETAILS,
      {}
    );
    const response = await api.post(endpoint, data);
    return response;
  },
  verifyJoiningDetails: async (data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.VERIFYJOININGDETAILS,
      {}
    );
    const response = await api.put(endpoint, data);
    return response;
  },
  getCandidateByContact: async ({ phone, email }) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.GETBYCONTACT, {
      phone: normalizeIndianPhone(phone),
      email,
    });
    const response = await api.get(endpoint);
    return response;
  },
  getCandidateProfile: async (id, opportunityId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.GETCANDIDATEDPROFILE,
      {
        id,
        opportunityId,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  downloadResumePdf: async (candidateId, masked) => {
    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.GETRESUMEPDF, {
      candidate_id: candidateId,
      masked: masked ? 'true' : 'false',
    });
    const response = await api.get(endpoint, { responseType: 'blob' });
    return response;
  },
  getCandidateAuditLogs: async ({
    candidateId,
    page = 1,
    pageSize = 10,
    search = '',
    eventCategory = '',
    actorType = '',
    sortBy = 'newest',
  }) => {
    // Map sortBy to proper API parameters
    const sortConfig =
      sortBy === 'newest'
        ? { sortBy: 'event_timestamp', sortOrder: 'desc' }
        : { sortBy: 'event_timestamp', sortOrder: 'asc' };

    const endpoint = createEndpoint(API_ENDPOINTS.CANDIDATES.GETAUDITLOGS, {
      id: candidateId,
      pageNo: page,
      pageSize,
      search,
      eventCategory,
      actorType,
      sortBy: sortConfig.sortBy,
      sortOrder: sortConfig.sortOrder,
    });
    const response = await api.get(endpoint);
    return response;
  },
  getAuditConfig: async () => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.GETAUDITCONFIG,
      {}
    );
    const response = await api.get(endpoint);
    return response;
  },
  refreshCandidateSession: async ({ candidateId, opportunityId }) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.REFRESHSESSION,
      {}
    );
    const response = await api.post(endpoint, {
      candidate_id: candidateId,
      requirement_id: opportunityId,
    });
    return response;
  },
  bulkUpdateFromTASubmission: async (opportunityId, data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.CANDIDATES.BULKUPDATEFROMTASUBMISSION,
      {
        id: opportunityId,
      }
    );
    const response = await api.put(endpoint, data);
    return response;
  },
};
